import {
  LSPClient,
  DEFAULT_CONFIG,
  SymbolKind,
  SymbolKindType,
} from "./lsp_core";
import { checkRealFuncChar } from "./symbol_utils";
import { detectDominantLanguages, getLanguageFileExtensions } from "./os_utils";
import { toFileUri, fileUriToPath } from "./file_utils";
import * as path from "path";
import * as fs from "fs";
import { glob } from "glob";

const logger = {
  trace: (...args: any[]) => console.error("TRACE", ...args),
  error: (...args: any[]) => console.error("ERROR", ...args),
};

export interface ReferenceTriple {
  caller: string;
  callee: string;
  location: string;
}

export interface SymbolReference {
  symbol: string;
  file: string;
  line: number;
  column: number;
  kind: string;
  callers: ReferenceTriple[];
  callees: ReferenceTriple[];
  definition?: {
    file: string;
    line: number;
    column: number;
    content: string;
  };
}

export async function analyzeProjectReferences(
  projectRoot: string,
): Promise<ReferenceTriple[]> {
  const rootUri = toFileUri(projectRoot);
  const client = new LSPClient(rootUri, {
    ...DEFAULT_CONFIG,
    timeout: 60000,
    logLevel: "debug",
  });

  const languages = detectDominantLanguages(projectRoot, {
    ignoredDirs: new Set(["node_modules", "dist"]),
    topN: 1,
  });
  const lang = languages[0];
  const file_ext = getLanguageFileExtensions(lang);

  try {
    logger.trace("Initializing LSP client...");
    await client.start(lang);

    const files = await glob(`**/*.{${file_ext}}`, {
      cwd: projectRoot,
      ignore: "node_modules/**",
      absolute: true,
    });

    const allReferences: ReferenceTriple[] = [];

    for (const filePath of files) {
      const fileUri = toFileUri(filePath);
      const relativePath = path.relative(projectRoot, filePath);

      try {
        const content = fs.readFileSync(filePath, "utf-8");
        await client.sendDidOpen(fileUri, content, "typescript");

        const symbols = await client.sendDocumentSymbol(fileUri);
        if (!symbols) continue;

        for (const symbol of symbols) {
          if (isAnalyzableSymbol(symbol.kind)) {
            const str_code = await client.readFile(fileUri);
            const lines = str_code.split("\n");
            const line = lines
              .slice(
                symbol.location.range.start.line,
                symbol.location.range.end.line + 1,
              )
              .join("\n");

            const pos = checkRealFuncChar(
              line,
              symbol.location.range.start.line,
              symbol.location.range.start.character,
              symbol.name,
              symbol.kind,
              fileUri,
            );

            if (pos.character < 0) continue;

            const refs = await client.sendReferences(
              fileUri,
              pos.line,
              pos.character,
            );

            if (refs?.result?.length) {
              for (const ref of refs.result) {
                allReferences.push(
                  createReferenceTriple(
                    projectRoot,
                    ref,
                    relativePath,
                    symbol,
                    pos,
                  ),
                );
              }
            }
          }
        }

        await client.sendDidClose(fileUri);
      } catch (err) {
        logger.error(`Error analyzing ${relativePath}:`, err);
      }
    }

    return allReferences;
  } finally {
    await client.shutdown();
  }
}

export async function analyzeSymbolReferences(
  projectRoot: string,
  symbolName: string,
  options: {
    includeExternal?: boolean;
    maxDepth?: number;
    caseSensitive?: boolean;
  } = {},
): Promise<SymbolReference[]> {
  const {
    includeExternal = false,
    maxDepth = 3,
    caseSensitive = false,
  } = options;

  const rootUri = toFileUri(projectRoot);
  const client = new LSPClient(rootUri, {
    ...DEFAULT_CONFIG,
    timeout: 60000,
    logLevel: "debug",
  });

  const languages = detectDominantLanguages(projectRoot, {
    ignoredDirs: new Set(["node_modules", "dist"]),
    topN: 1,
  });
  const lang = languages[0];
  const file_ext = getLanguageFileExtensions(lang);

  try {
    logger.trace("Initializing LSP client...");
    await client.start(lang);

    const files = await glob(`**/*.{${file_ext}}`, {
      cwd: projectRoot,
      ignore: "node_modules/**",
      absolute: true,
    });

    const symbolReferences: SymbolReference[] = [];
    const allReferences: ReferenceTriple[] = [];

    // 第一步：收集所有引用关系
    for (const filePath of files) {
      const fileUri = toFileUri(filePath);
      const relativePath = path.relative(projectRoot, filePath);

      try {
        const content = fs.readFileSync(filePath, "utf-8");
        await client.sendDidOpen(fileUri, content, "typescript");

        const symbols = await client.sendDocumentSymbol(fileUri);
        if (!symbols) continue;

        for (const symbol of symbols) {
          if (isAnalyzableSymbol(symbol.kind)) {
            const str_code = await client.readFile(fileUri);
            const lines = str_code.split("\n");
            const line = lines
              .slice(
                symbol.location.range.start.line,
                symbol.location.range.end.line + 1,
              )
              .join("\n");

            const pos = checkRealFuncChar(
              line,
              symbol.location.range.start.line,
              symbol.location.range.start.character,
              symbol.name,
              symbol.kind,
              fileUri,
            );

            if (pos.character < 0) continue;

            const refs = await client.sendReferences(
              fileUri,
              pos.line,
              pos.character,
            );

            if (refs?.result?.length) {
              for (const ref of refs.result) {
                allReferences.push(
                  createReferenceTriple(
                    projectRoot,
                    ref,
                    relativePath,
                    symbol,
                    pos,
                  ),
                );
              }
            }
          }
        }

        await client.sendDidClose(fileUri);
      } catch (err) {
        logger.error(`Error analyzing ${relativePath}:`, err);
      }
    }

    // 第二步：查找匹配的符号
    for (const filePath of files) {
      const fileUri = toFileUri(filePath);
      const relativePath = path.relative(projectRoot, filePath);

      try {
        const content = fs.readFileSync(filePath, "utf-8");
        await client.sendDidOpen(fileUri, content, "typescript");

        const symbols = await client.sendDocumentSymbol(fileUri);
        if (!symbols) continue;

        for (const symbol of symbols) {
          if (isAnalyzableSymbol(symbol.kind)) {
            const symbolMatch = caseSensitive
              ? symbol.name === symbolName
              : symbol.name.toLowerCase() === symbolName.toLowerCase();

            if (symbolMatch) {
              const str_code = await client.readFile(fileUri);
              const lines = str_code.split("\n");
              const line = lines
                .slice(
                  symbol.location.range.start.line,
                  symbol.location.range.end.line + 1,
                )
                .join("\n");

              const pos = checkRealFuncChar(
                line,
                symbol.location.range.start.line,
                symbol.location.range.start.character,
                symbol.name,
                symbol.kind,
                fileUri,
              );

              if (pos.character < 0) continue;

              // 查找调用方（谁调用了这个符号）
              const callers = allReferences.filter(
                (ref) => ref.callee === `${relativePath}:${symbol.name}`,
              );

              // 查找被调用方（这个符号调用了谁）
              const callees = allReferences.filter(
                (ref) => ref.caller === `${relativePath}:${symbol.name}`,
              );

              // 过滤外部调用
              const filteredCallers = includeExternal
                ? callers
                : callers.filter((ref) => !ref.caller.includes("node_modules"));

              const filteredCallees = includeExternal
                ? callees
                : callees.filter((ref) => !ref.callee.includes("node_modules"));

              symbolReferences.push({
                symbol: symbol.name,
                file: relativePath,
                line: pos.line + 1,
                column: pos.character + 1,
                kind: getSymbolKindName(symbol.kind),
                callers: filteredCallers,
                callees: filteredCallees,
                definition: {
                  file: relativePath,
                  line: pos.line + 1,
                  column: pos.character + 1,
                  content: line.trim(),
                },
              });
            }
          }
        }

        await client.sendDidClose(fileUri);
      } catch (err) {
        logger.error(`Error analyzing ${relativePath}:`, err);
      }
    }

    return symbolReferences;
  } finally {
    await client.shutdown();
  }
}

// Helper functions

function isAnalyzableSymbol(kind: SymbolKindType): boolean {
  const analyzableKinds = new Set<SymbolKindType>([
    SymbolKind.Function,
    SymbolKind.Method,
    SymbolKind.Class,
    SymbolKind.Interface,
    SymbolKind.Property,
  ]);
  return analyzableKinds.has(kind);
}

function createReferenceTriple(
  projectRoot: string,
  ref: any,
  relativePath: string,
  symbol: any,
  pos: any,
): ReferenceTriple {
  const absolutePath = fileUriToPath(ref.uri);
  const callerFile = path.relative(projectRoot, absolutePath);
  const callerPos = `${ref.range.start.line + 1}:${ref.range.start.character + 1}`;

  return {
    caller: `${callerFile}:${callerPos}`,
    callee: `${relativePath}:${symbol.name}`,
    location: `${relativePath}:${pos.line + 1}:${pos.character + 1}`,
  };
}

function getSymbolKindName(kind: SymbolKindType): string {
  switch (kind) {
    case SymbolKind.Function:
      return "function";
    case SymbolKind.Method:
      return "method";
    case SymbolKind.Class:
      return "class";
    case SymbolKind.Interface:
      return "interface";
    case SymbolKind.Property:
      return "property";
    case SymbolKind.Variable:
      return "variable";
    case SymbolKind.Constant:
      return "constant";
    case SymbolKind.Constructor:
      return "constructor";
    case SymbolKind.Field:
      return "field";
    default:
      return "unknown";
  }
}
